import { useState } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';

export default function NotificationSettingsPage() {
  const [emailNotifications, setEmailNotifications] = useState({
    taskReminders: true,
    deadlineAlerts: true,
    timeTrackerReminders: false,
    clientMessages: true,
    paymentReminders: true,
    weeklyReports: true,
    monthlyReports: false,
    systemUpdates: true,
    securityAlerts: true,
    marketingEmails: false,
  });

  const [pushNotifications, setPushNotifications] = useState({
    taskReminders: true,
    deadlineAlerts: true,
    timeTrackerReminders: true,
    clientMessages: true,
    paymentReminders: false,
    systemAlerts: true,
  });

  const [notificationTiming, setNotificationTiming] = useState({
    workingHoursOnly: true,
    startTime: '09:00',
    endTime: '18:00',
    timezone: 'America/New_York',
    quietDays: ['saturday', 'sunday'],
  });

  const [digestSettings, setDigestSettings] = useState({
    dailyDigest: true,
    dailyTime: '08:00',
    weeklyDigest: true,
    weeklyDay: 'monday',
    weeklyTime: '09:00',
  });

  const handleEmailNotificationChange = (key: string, value: boolean) => {
    setEmailNotifications((prev) => ({ ...prev, [key]: value }));
  };

  const handlePushNotificationChange = (key: string, value: boolean) => {
    setPushNotifications((prev) => ({ ...prev, [key]: value }));
  };

  const handleTimingChange = (key: string, value: string | boolean | string[]) => {
    setNotificationTiming((prev) => ({ ...prev, [key]: value }));
  };

  const handleDigestChange = (key: string, value: string | boolean) => {
    setDigestSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleQuietDayToggle = (day: string) => {
    setNotificationTiming((prev) => ({
      ...prev,
      quietDays: prev.quietDays.includes(day)
        ? prev.quietDays.filter((d) => d !== day)
        : [...prev.quietDays, day],
    }));
  };

  const handleSave = () => {
    console.log('Saving notification settings:', {
      emailNotifications,
      pushNotifications,
      notificationTiming,
      digestSettings,
    });
  };

  const testNotification = () => {
    console.log('Sending test notification');
    // In real app, this would send a test notification
  };

  return (
    <AppLayout>
      <Breadcrumb items={[{ label: 'Settings', href: '/settings' }, { label: 'Notifications' }]} />

      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Notification Settings</h1>
            <p className="text-gray-600">Customize when and how you receive notifications</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={testNotification}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-white/30 transition-colors"
            >
              Test Notification
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90 transition-colors"
            >
              Save Changes
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Email Notifications */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Notifications</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Work & Tasks</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">Task Reminders</div>
                        <div className="text-sm text-gray-600">
                          Get reminded about upcoming tasks
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={emailNotifications.taskReminders}
                          onChange={(e) =>
                            handleEmailNotificationChange('taskReminders', e.target.checked)
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">Deadline Alerts</div>
                        <div className="text-sm text-gray-600">
                          Alerts for approaching deadlines
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={emailNotifications.deadlineAlerts}
                          onChange={(e) =>
                            handleEmailNotificationChange('deadlineAlerts', e.target.checked)
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">Time Tracker Reminders</div>
                        <div className="text-sm text-gray-600">
                          Reminders to start/stop tracking
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={emailNotifications.timeTrackerReminders}
                          onChange={(e) =>
                            handleEmailNotificationChange('timeTrackerReminders', e.target.checked)
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Business & Clients</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">Client Messages</div>
                        <div className="text-sm text-gray-600">New messages from clients</div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={emailNotifications.clientMessages}
                          onChange={(e) =>
                            handleEmailNotificationChange('clientMessages', e.target.checked)
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                      </label>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-gray-900">Payment Reminders</div>
                        <div className="text-sm text-gray-600">
                          Invoice and payment notifications
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={emailNotifications.paymentReminders}
                          onChange={(e) =>
                            handleEmailNotificationChange('paymentReminders', e.target.checked)
                          }
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <hr className="border-gray-200" />

              <div>
                <h4 className="font-medium text-gray-900 mb-3">Reports & Updates</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">Weekly Reports</div>
                      <div className="text-sm text-gray-600">Weekly productivity summaries</div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailNotifications.weeklyReports}
                        onChange={(e) =>
                          handleEmailNotificationChange('weeklyReports', e.target.checked)
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">Monthly Reports</div>
                      <div className="text-sm text-gray-600">Monthly business insights</div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailNotifications.monthlyReports}
                        onChange={(e) =>
                          handleEmailNotificationChange('monthlyReports', e.target.checked)
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">System Updates</div>
                      <div className="text-sm text-gray-600">App updates and new features</div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailNotifications.systemUpdates}
                        onChange={(e) =>
                          handleEmailNotificationChange('systemUpdates', e.target.checked)
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">Security Alerts</div>
                      <div className="text-sm text-gray-600">Account security notifications</div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={emailNotifications.securityAlerts}
                        onChange={(e) =>
                          handleEmailNotificationChange('securityAlerts', e.target.checked)
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Push Notifications */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Push Notifications</h3>
            <p className="text-sm text-gray-600 mb-4">
              Real-time notifications delivered to your browser or mobile device
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                {Object.entries(pushNotifications).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <div className="font-medium text-gray-900 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => handlePushNotificationChange(key, e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-flowky-primary"></div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Notification Timing */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Timing</h3>
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="workingHoursOnly"
                    checked={notificationTiming.workingHoursOnly}
                    onChange={(e) => handleTimingChange('workingHoursOnly', e.target.checked)}
                    className="w-4 h-4 text-flowky-primary border-gray-300 rounded focus:ring-flowky-primary"
                  />
                  <label htmlFor="workingHoursOnly" className="font-medium text-gray-900">
                    Only send notifications during working hours
                  </label>
                </div>

                {notificationTiming.workingHoursOnly && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ml-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time
                      </label>
                      <input
                        type="time"
                        value={notificationTiming.startTime}
                        onChange={(e) => handleTimingChange('startTime', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Time
                      </label>
                      <input
                        type="time"
                        value={notificationTiming.endTime}
                        onChange={(e) => handleTimingChange('endTime', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Timezone
                      </label>
                      <select
                        value={notificationTiming.timezone}
                        onChange={(e) => handleTimingChange('timezone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                      >
                        <option value="America/New_York">Eastern Time</option>
                        <option value="America/Chicago">Central Time</option>
                        <option value="America/Denver">Mountain Time</option>
                        <option value="America/Los_Angeles">Pacific Time</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-3">Quiet Days</h4>
                <p className="text-sm text-gray-600 mb-3">
                  Select days when you don&apos;t want to receive non-urgent notifications
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    'monday',
                    'tuesday',
                    'wednesday',
                    'thursday',
                    'friday',
                    'saturday',
                    'sunday',
                  ].map((day) => (
                    <button
                      key={day}
                      onClick={() => handleQuietDayToggle(day)}
                      className={`px-3 py-2 rounded-lg border transition-colors capitalize ${
                        notificationTiming.quietDays.includes(day)
                          ? 'bg-flowky-primary text-white border-flowky-primary'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Digest Settings */}
          <div className="border border-border rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Digests</h3>
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="dailyDigest"
                    checked={digestSettings.dailyDigest}
                    onChange={(e) => handleDigestChange('dailyDigest', e.target.checked)}
                    className="w-4 h-4 text-flowky-primary border-gray-300 rounded focus:ring-flowky-primary"
                  />
                  <label htmlFor="dailyDigest" className="font-medium text-gray-900">
                    Daily Digest
                  </label>
                </div>
                {digestSettings.dailyDigest && (
                  <div className="ml-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Send at</label>
                    <input
                      type="time"
                      value={digestSettings.dailyTime}
                      onChange={(e) => handleDigestChange('dailyTime', e.target.value)}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                    />
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="weeklyDigest"
                    checked={digestSettings.weeklyDigest}
                    onChange={(e) => handleDigestChange('weeklyDigest', e.target.checked)}
                    className="w-4 h-4 text-flowky-primary border-gray-300 rounded focus:ring-flowky-primary"
                  />
                  <label htmlFor="weeklyDigest" className="font-medium text-gray-900">
                    Weekly Digest
                  </label>
                </div>
                {digestSettings.weeklyDigest && (
                  <div className="ml-6 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Day</label>
                      <select
                        value={digestSettings.weeklyDay}
                        onChange={(e) => handleDigestChange('weeklyDay', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                      >
                        <option value="monday">Monday</option>
                        <option value="tuesday">Tuesday</option>
                        <option value="wednesday">Wednesday</option>
                        <option value="thursday">Thursday</option>
                        <option value="friday">Friday</option>
                        <option value="saturday">Saturday</option>
                        <option value="sunday">Sunday</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                      <input
                        type="time"
                        value={digestSettings.weeklyTime}
                        onChange={(e) => handleDigestChange('weeklyTime', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-flowky-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
